import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  API_ROUTES,
  SUDOKU_DIFFICULTIES,
  SUDOKU_DIFFICULTY_LABELS,
  SUDOKU_DIGITS,
  SUDOKU_SIZE,
  clearSelectedCell,
  fillAllButOne,
  finalizeWin,
  formatSudokuTime,
  giveHint,
  navigateCell,
  newSudokuGame,
  placeDigit,
  selectCell,
  sudokuCellView,
  toggleNotesMode,
  validateFilled,
  type SudokuDifficulty,
  type SudokuScore,
  type SudokuState,
} from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { formatDate } from "../lib/format.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import { SudokuBoard } from "../games/sudoku/SudokuBoard.js";
import { ADMIN_LIST, AdminDeleteButton, useAdminListTools } from "../components/admin/AdminChrome.js";
import "../styles/sudoku.css";

type FlashKind = "correct" | "error" | "hint";

export function SudokuPage() {
  const { user } = useSession();
  const { push } = useFlash();
  const { busy, submit } = useFormSubmit();
  const [difficulty, setDifficulty] = useState<SudokuDifficulty>("easy");
  const [state, setState] = useState<SudokuState | null>(null);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [notesOn, setNotesOn] = useState(false);
  const [flash, setFlash] = useState<{ index: number; kind: FlashKind } | null>(null);
  const [validateFlash, setValidateFlash] = useState<{
    errors: number[];
    corrects: number[];
  } | null>(null);
  const [won, setWon] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [saved, setSaved] = useState(false);
  const [boardTab, setBoardTab] = useState<SudokuDifficulty>("easy");
  const scores = useLeaderboard(boardTab);
  const stateRef = useRef(state);
  const startMs = useRef(0);
  const elapsedRef = useRef(0);

  const commit = useCallback((next: SudokuState | null) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const ticking = Boolean(state?.gameActive) && !won;

  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startMs.current) / 1000);
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => window.clearInterval(id);
  }, [ticking]);

  useEffect(() => {
    if (user && !playerName) setPlayerName(user.username);
  }, [user, playerName]);

  const cells = useMemo(() => {
    if (!state) return [];
    return Array.from({ length: SUDOKU_SIZE }, (_, index) => sudokuCellView(state, index));
  }, [state]);

  const finishIfWon = useCallback((next: SudokuState, didWin: boolean) => {
    if (!didWin) {
      commit(next);
      return;
    }
    const elapsed = Math.floor((Date.now() - startMs.current) / 1000);
    elapsedRef.current = elapsed;
    setElapsedSec(elapsed);
    commit(finalizeWin(next, elapsed));
    setWon(true);
  }, [commit]);

  const startGame = useCallback(
    (level: SudokuDifficulty) => {
      setGenerating(true);
      window.setTimeout(() => {
        let game = newSudokuGame(level);
        if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("almost") === "1") {
          game = fillAllButOne(game);
        }
        startMs.current = Date.now();
        elapsedRef.current = 0;
        commit(game);
        setDifficulty(level);
        setElapsedSec(0);
        setNotesOn(false);
        setFlash(null);
        setValidateFlash(null);
        setWon(false);
        setSaved(false);
        setGenerating(false);
      }, 20);
    },
    [commit],
  );

  const onSelect = useCallback((index: number) => {
    const current = stateRef.current;
    if (!current) return;
    commit(selectCell(current, index));
  }, [commit]);

  const onDigit = useCallback(
    (digit: (typeof SUDOKU_DIGITS)[number]) => {
      const current = stateRef.current;
      if (!current) return;
      const result = placeDigit(current, digit);
      if (!result.ok) return;
      if (result.flash && current.selectedCell !== null) {
        setFlash({ index: current.selectedCell, kind: result.flash });
        window.setTimeout(() => setFlash(null), 500);
      }
      finishIfWon(result.state, result.won);
    },
    [finishIfWon],
  );

  const onClear = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    commit(clearSelectedCell(current));
  }, [commit]);

  const onHint = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    const result = giveHint(current);
    if (!result.ok) {
      if (result.reason === "none") push("Brak dostępnych podpowiedzi!", "error");
      return;
    }
    setFlash({ index: result.index, kind: "hint" });
    window.setTimeout(() => setFlash(null), 1000);
    finishIfWon(result.state, result.won);
  }, [finishIfWon, push]);

  const onValidate = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    const result = validateFilled(current);
    setValidateFlash({ errors: result.errorIndexes, corrects: result.correctIndexes });
    window.setTimeout(() => setValidateFlash(null), 1500);
    if (result.hasErrors) {
      push(`Znaleziono błędy! Poprawnych: ${result.correctCount}`, "error");
    } else if (result.correctCount > 0) {
      push("Świetnie! Wszystkie wypełnione pola są poprawne!", "success");
    } else {
      push("Wypełnij kilka pól, aby je sprawdzić", "info");
    }
  }, [push]);

  const onNotes = useCallback(() => {
    const current = stateRef.current;
    if (!current) return;
    const next = toggleNotesMode(current);
    commit(next);
    setNotesOn(next.notesMode);
  }, [commit]);

  const backToMenu = useCallback(() => {
    if (stateRef.current?.gameActive && !window.confirm("Opuścić grę? Postęp zostanie utracony.")) {
      return;
    }
    commit(null);
    setWon(false);
  }, [commit]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const current = stateRef.current;
      if (!current?.gameActive) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      if (event.key >= "1" && event.key <= "9") {
        event.preventDefault();
        onDigit(Number(event.key) as (typeof SUDOKU_DIGITS)[number]);
        return;
      }
      if (event.key === "Backspace" || event.key === "Delete" || event.key === "0") {
        event.preventDefault();
        onClear();
        return;
      }
      if (
        event.key === "ArrowUp" ||
        event.key === "ArrowDown" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight"
      ) {
        event.preventDefault();
        commit(navigateCell(current, event.key));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commit, onClear, onDigit]);

  async function saveScore() {
    const current = stateRef.current;
    if (!current) return;
    const name = (user?.username ?? playerName).trim();
    if (!name) {
      push("Proszę wpisać imię!", "error");
      return;
    }
    const ok = await submit(async () => {
      await apiRequest<SudokuScore>(API_ROUTES.sudoku.scores, {
        method: "POST",
        body: {
          name,
          difficulty: current.difficulty,
          time: elapsedRef.current,
          score: current.score,
          hintsUsed: current.hintsUsed,
          mistakes: current.mistakes,
          puzzle: current.puzzle,
          solution: current.solution,
          board: current.board,
        },
      });
      setBoardTab(current.difficulty);
      await scores.reload(current.difficulty);
    }, "Wynik zapisany!");
    if (ok) setSaved(true);
  }

  if (!state) {
    return (
      <div className="sudoku">
        <section className="sudoku__play">
          <div className="sudoku-start">
            <h2 className="sudoku__title">
              <i className="z-icon" aria-hidden="true">
                🧩
              </i>
              Sudoku
            </h2>
            <div className="sudoku-diff" role="group" aria-label="Poziom trudności">
              {SUDOKU_DIFFICULTIES.map((level) => (
                <Button
                  key={level}
                  variant={difficulty === level ? "primary" : "default"}
                  onClick={() => setDifficulty(level)}
                >
                  {SUDOKU_DIFFICULTY_LABELS[level]}
                </Button>
              ))}
            </div>
            <Button
              variant="primary"
              disabled={generating}
              data-sudoku="start"
              onClick={() => startGame(difficulty)}
            >
              {generating ? "Generowanie..." : "Start gry"}
            </Button>
          </div>
        </section>
        <SudokuLeaderboard
          tab={boardTab}
          onTab={setBoardTab}
          scores={scores}
        />
      </div>
    );
  }

  return (
    <div className="sudoku">
      <section className="sudoku__play">
        <header className="sudoku__header">
          <h2 className="sudoku__title">
            <i className="z-icon" aria-hidden="true">
              🧩
            </i>
            Sudoku · {SUDOKU_DIFFICULTY_LABELS[state.difficulty]}
          </h2>
          <div className="sudoku__controls">
            <Button onClick={backToMenu}>Menu</Button>
            <Button onClick={() => startGame(state.difficulty)} disabled={generating}>
              Nowa gra
            </Button>
            <div className="sudoku__stats" aria-live="polite">
              <span>
                Czas: <strong>{formatSudokuTime(elapsedSec)}</strong>
              </span>
              <span>
                Wynik: <strong>{state.score}</strong>
              </span>
              <span>
                Błędy: <strong>{state.mistakes}</strong>
              </span>
              <span>
                Podpowiedzi: <strong>{state.hintsRemaining}</strong>
              </span>
            </div>
          </div>
        </header>

        <SudokuBoard
          cells={cells}
          flash={flash}
          validateFlash={validateFlash}
          onSelect={onSelect}
        />

        <div className="sudoku-actions">
          <Button variant={notesOn ? "primary" : "default"} data-sudoku="notes" onClick={onNotes}>
            Notatki
          </Button>
          <Button
            data-sudoku="hint"
            onClick={onHint}
            disabled={state.hintsRemaining <= 0 || !state.gameActive}
          >
            Podpowiedź
          </Button>
          <Button data-sudoku="check" onClick={onValidate} disabled={!state.gameActive}>
            Sprawdź
          </Button>
          <Button data-sudoku="clear" onClick={onClear} disabled={!state.gameActive}>
            Wyczyść
          </Button>
        </div>

        <div className="sudoku-pad" role="group" aria-label="Klawiatura cyfr">
          {SUDOKU_DIGITS.map((digit) => (
            <Button key={digit} disabled={!state.gameActive} onClick={() => onDigit(digit)}>
              {digit}
            </Button>
          ))}
        </div>
      </section>

      <SudokuLeaderboard tab={boardTab} onTab={setBoardTab} scores={scores} />

      {won ? (
        <div className="sudoku-overlay" role="dialog" aria-modal="true" aria-labelledby="sudoku-win-title">
          <div className="sudoku-overlay__panel">
            <h2 id="sudoku-win-title">🎉 Gratulacje!</h2>
            <p>Ukończyłeś sudoku!</p>
            <ul className="sudoku-overlay__stats">
              <li>
                Trudność: <strong>{SUDOKU_DIFFICULTY_LABELS[state.difficulty]}</strong>
              </li>
              <li>
                Czas: <strong>{formatSudokuTime(elapsedSec)}</strong>
              </li>
              <li>
                Wynik: <strong>{state.score}</strong>
              </li>
              <li>
                Błędy: <strong>{state.mistakes}</strong>
              </li>
              <li>
                Użyte podpowiedzi: <strong>{state.hintsUsed}</strong>
              </li>
            </ul>
            {saved ? (
              <p className="hub-hint">Wynik jest już na tablicy.</p>
            ) : (
              <label className="sudoku-name">
                <span>Imię</span>
                <input
                  className="z-input"
                  value={user ? user.username : playerName}
                  maxLength={20}
                  placeholder="Wpisz swoje imię"
                  readOnly={Boolean(user)}
                  onChange={(event) => {
                    if (!user) setPlayerName(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void saveScore();
                  }}
                />
              </label>
            )}
            <div className="sudoku-overlay__actions">
              {!saved ? (
                <Button variant="primary" disabled={busy} onClick={() => void saveScore()}>
                  {busy ? "Zapisywanie..." : "Zapisz wynik"}
                </Button>
              ) : null}
              <Button
                variant={saved ? "primary" : "ghost"}
                onClick={() => {
                  commit(null);
                  setWon(false);
                }}
              >
                Zagraj ponownie
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SudokuLeaderboard({
  tab,
  onTab,
  scores,
}: {
  tab: SudokuDifficulty;
  onTab: (level: SudokuDifficulty) => void;
  scores: ReturnType<typeof useLeaderboard>;
}) {
  const admin = useAdminListTools({
    ...ADMIN_LIST.sudoku,
    onReload: () => scores.reload(tab),
  });

  return (
    <aside className="sudoku__scores">
      <Card variant="panel" title="Najlepsze wyniki" icon="⭐">
        <div className="sudoku-tabs" role="tablist" aria-label="Ranking według poziomu">
          {SUDOKU_DIFFICULTIES.map((level) => (
            <Button
              key={level}
              size="sm"
              variant={tab === level ? "primary" : "ghost"}
              onClick={() => onTab(level)}
            >
              {SUDOKU_DIFFICULTY_LABELS[level]}
            </Button>
          ))}
        </div>
        {admin.isAdmin ? (
          <div className="admin-onpage__toolbar">
            <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void admin.wipe()}>
              Wyczyść tablicę
            </Button>
          </div>
        ) : null}
        {scores.error ? <p className="hub-error">{scores.error}</p> : null}
        {scores.loading ? (
          <p className="hub-hint">Ładowanie...</p>
        ) : scores.items.length === 0 ? (
          <p className="hub-hint">Brak wyników</p>
        ) : (
          <ol className="sudoku-leaderboard">
            {scores.items.map((entry, index) => (
              <li
                key={entry.id}
                className={`sudoku-leaderboard__row${admin.isAdmin ? " sudoku-leaderboard__row--admin" : ""}`}
              >
                <span className="sudoku-leaderboard__rank">{index + 1}.</span>
                <span className="sudoku-leaderboard__name">{entry.username}</span>
                <span className="sudoku-leaderboard__time">{formatSudokuTime(entry.time)}</span>
                <span className="sudoku-leaderboard__score">{entry.score}</span>
                <span className="sudoku-leaderboard__date">{formatDate(entry.createdAt)}</span>
                {admin.isAdmin ? (
                  <AdminDeleteButton
                    confirm="Usunąć ten wynik?"
                    disabled={admin.busy}
                    onConfirm={() => admin.remove(entry.id)}
                  />
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </aside>
  );
}

function useLeaderboard(difficulty: SudokuDifficulty) {
  const [items, setItems] = useState<SudokuScore[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const difficultyRef = useRef(difficulty);
  difficultyRef.current = difficulty;

  const reload = useCallback(async (level: SudokuDifficulty = difficultyRef.current) => {
    try {
      const next = await apiRequest<SudokuScore[]>(
        `${API_ROUTES.sudoku.scores}?difficulty=${level}`,
      );
      setItems(next);
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload(difficulty);
  }, [difficulty, reload]);

  return { items, error, loading, reload };
}
